/**
 * TypeScript port of `noodle.scene.terrain.TerrainBuilder.buildTileMesh` (Kotlin).
 *
 * Generates one terrain tile's vertex data (positions + analytic normals + zero UVs)
 * plus a triangle index buffer. The output is an indexed mesh — neighbouring triangles
 * share corner vertices, unlike the backend's GLB writer which currently emits unshared
 * triangles. This is what cuts ~3× of the data size while remaining visually identical.
 *
 * Inputs match the backend exactly:
 *  - `cellSizeGlobal = max(width, depth) / resolution` (resolution clamped 8..4096)
 *  - per-LOD grid: `gridWidth = max(1, round(tileWidth / cellSizeGlobal) >> lod)`
 *  - LOD>0 height = max-pool over a (2*radius+1)² grid of LOD0-resolution taps
 *  - normals: central differences with `eps = max(cellSizeX, cellSizeZ) * 0.5`
 *  - skirts: vertical drop quads at every tile-perimeter edge, depth =
 *    `max(cellSizeX, cellSizeZ) * 4`
 *
 * Surface blending is now handled by the per-tile RGBA8 index map rasterized from
 * polygon regions client-side — COLOR_0 slot weights are no longer needed.
 */

export const MIN_RESOLUTION = 8;
export const MAX_RESOLUTION = 4096;

/**
 * (x, z) → height. Decoupled from `HeightField` so the worker can construct
 * the sampler from raw heightmap pixels without pulling in the engine's
 * Ray/HeightField types.
 */
export type HeightSampleFn = (x: number, z: number) => number;

/**
 * Closed XZ polygon describing a terrain "hole". Stored as an interleaved
 * Float32Array (x0, z0, x1, z1, ...) so meshing can iterate without per-call
 * boxing — at large brush radius the inner loop is the hot path.
 */
export interface HolePolygon {
  /** Interleaved `[x0, z0, x1, z1, ...]`, ≥ 3 vertices (6 floats). */
  vertices: Float32Array;
  /** XZ AABB for fast rejection: [minX, minZ, maxX, maxZ]. */
  aabb: [number, number, number, number];
}

/**
 * Pack manifest-style hole polygon refs (`{id, vertices: [[x,z], ...]}`) into
 * the interleaved Float32 + cached AABB shape the mesh generator + worker
 * consume. Shared by both the runtime manifest loader and the editor's
 * streamer-side install path so the wire format change doesn't fork.
 */
export function buildHolePolygons(
  refs: ReadonlyArray<{ vertices: readonly (readonly [number, number])[] }>,
): HolePolygon[] {
  const out: HolePolygon[] = [];
  for (const h of refs) {
    if (!h.vertices || h.vertices.length < 3) continue;
    const verts = new Float32Array(h.vertices.length * 2);
    let minX = Infinity, minZ = Infinity, maxX = -Infinity, maxZ = -Infinity;
    for (let i = 0; i < h.vertices.length; i++) {
      const x = h.vertices[i][0];
      const z = h.vertices[i][1];
      verts[i * 2] = x;
      verts[i * 2 + 1] = z;
      if (x < minX) minX = x;
      if (z < minZ) minZ = z;
      if (x > maxX) maxX = x;
      if (z > maxZ) maxZ = z;
    }
    out.push({ vertices: verts, aabb: [minX, minZ, maxX, maxZ] });
  }
  return out;
}

export interface TileMeshConfig {
  /** Authoring `width` (X extent in metres). Used only to derive cellSizeGlobal. */
  width: number;
  /** Authoring `depth` (Z extent in metres). */
  depth: number;
  /** Authoring `resolution`; clamped to [MIN_RESOLUTION, MAX_RESOLUTION]. */
  resolution: number;
  /** Seed (unused at runtime, kept for worker protocol stability). */
  seed: number | bigint;
}

export interface TileBounds {
  tileX: number;
  tileZ: number;
  worldMinX: number;
  worldMinZ: number;
  worldMaxX: number;
  worldMaxZ: number;
}

export interface TileMeshOutput {
  /** Vec3 per vertex. */
  positions: Float32Array;
  /** Vec3 per vertex (analytic central-difference normals). */
  normals: Float32Array;
  /** Vec2 per vertex (always zero — terrain shader is triplanar). */
  uvs: Float32Array;
  /**
   * Triangle indices. uint16 when index count fits (≤ 65535) so the renderer's
   * `itemCount > 65535 ? UNSIGNED_INT : UNSIGNED_SHORT` heuristic in
   * {@link ../../mesh/VertexBuffer} reads the right element size; uint32 above.
   */
  indices: Uint16Array | Uint32Array;
  /** Total vertex count (positions.length / 3). */
  vertexCount: number;
  /** Total triangle count (indices.length / 3). */
  triangleCount: number;
}

/**
 * Build one terrain tile mesh at the requested LOD. Pure CPU work — no GL,
 * no DOM. Safe to run inside a Web Worker.
 *
 * `holes` (optional) is the list of terrain hole polygons; quads whose
 * center falls inside any of them are skipped (their triangles are not
 * emitted into the index buffer). Holes whose AABB doesn't overlap the
 * tile are filtered away ahead of the inner loop so the typical
 * empty-hole-list case is near-zero overhead.
 */
export function generateTileMesh(
  config: TileMeshConfig,
  bounds: TileBounds,
  heightSample: HeightSampleFn,
  lodLevel: number,
  holes?: readonly HolePolygon[],
): TileMeshOutput {
  const resolution = clamp(config.resolution, MIN_RESOLUTION, MAX_RESOLUTION);
  const cellSizeGlobal = Math.max(config.width, config.depth) / resolution;

  const tileWidth = bounds.worldMaxX - bounds.worldMinX;
  const tileDepth = bounds.worldMaxZ - bounds.worldMinZ;

  const lod = Math.max(0, lodLevel);
  const lodDivisor = 1 << lod;
  const gridWidth = Math.max(1, Math.trunc(tileWidth / cellSizeGlobal + 0.5) / lodDivisor | 0);
  const gridHeight = Math.max(1, Math.trunc(tileDepth / cellSizeGlobal + 0.5) / lodDivisor | 0);

  const cellSizeX = tileWidth / gridWidth;
  const cellSizeZ = tileDepth / gridHeight;
  const sampleEps = Math.max(cellSizeX, cellSizeZ) * 0.5;
  const poolRadius = (lodDivisor / 2) | 0;

  const cols = gridWidth + 1;
  const rows = gridHeight + 1;
  const baseVertCount = cols * rows;

  // Skirt vertices: the four perimeter edges' parent verts duplicated downward.
  // South + North have `cols` verts each; West + East have `rows - 2` (corners
  // owned by S/N). Total skirt verts = 2 * cols + 2 * (rows - 2) = 2 * cols + 2 * rows - 4.
  const skirtVertCount = 2 * cols + 2 * rows - 4;
  const totalVertCount = baseVertCount + skirtVertCount;

  const positions = new Float32Array(totalVertCount * 3);
  const normals = new Float32Array(totalVertCount * 3);
  const uvs = new Float32Array(totalVertCount * 2); // zeros

  // 1. Generate base grid positions (Y from height field, with optional max-pool).
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      const x = bounds.worldMinX + col * cellSizeX;
      const z = bounds.worldMinZ + row * cellSizeZ;
      const y = poolRadius > 0
        ? sampleMaxPooled(heightSample, x, z, poolRadius, cellSizeGlobal)
        : heightSample(x, z);
      const idx = (row * cols + col) * 3;
      positions[idx] = x;
      positions[idx + 1] = y;
      positions[idx + 2] = z;
    }
  }

  // 2. Per-vertex analytic normals via central differences.
  applyAnalyticNormals(positions, normals, baseVertCount, heightSample, sampleEps);

  // 3. Triangle indices for the base grid (CCW from above to match VertexNet.kt).
  const baseTriCount = gridWidth * gridHeight * 2;
  const skirtTriCount = 2 * (gridWidth + gridHeight) * 2; // 4 perimeter edges, 2 tris each
  const totalTriCount = baseTriCount + skirtTriCount;
  const totalIndexCount = totalTriCount * 3;
  // Match VertexBuffer.getIndexElementType — uint16 when count ≤ 65535, else uint32.
  const indices: Uint16Array | Uint32Array =
    totalIndexCount <= 65535
      ? new Uint16Array(totalIndexCount)
      : new Uint32Array(totalIndexCount);
  let triIdx = 0;
  const indexAt = (col: number, row: number): number => row * cols + col;
  // Pre-filter holes to only those whose AABB overlaps this tile so the
  // per-quad center test in the inner loop has the shortest candidate list.
  const activeHoles =
    holes && holes.length > 0
      ? holes.filter(
          (h) =>
            !(
              h.aabb[2] < bounds.worldMinX ||
              h.aabb[0] > bounds.worldMaxX ||
              h.aabb[3] < bounds.worldMinZ ||
              h.aabb[1] > bounds.worldMaxZ
            ),
        )
      : null;
  for (let row = 0; row < gridHeight; row++) {
    for (let col = 0; col < gridWidth; col++) {
      // Skip the quad entirely when its center falls inside any hole. We test
      // the center (not all four corners) so a hole whose rim threads between
      // vertices still cuts at single-quad resolution — fine since the hole
      // is typically filled by a placed cave-mouth GLB anyway.
      if (activeHoles !== null && activeHoles.length > 0) {
        const cx = bounds.worldMinX + (col + 0.5) * cellSizeX;
        const cz = bounds.worldMinZ + (row + 0.5) * cellSizeZ;
        let inHole = false;
        for (const h of activeHoles) {
          if (
            cx < h.aabb[0] ||
            cx > h.aabb[2] ||
            cz < h.aabb[1] ||
            cz > h.aabb[3]
          ) {
            continue;
          }
          if (pointInPolygonXZ(cx, cz, h.vertices)) {
            inHole = true;
            break;
          }
        }
        if (inHole) continue;
      }
      const i00 = indexAt(col, row);
      const i01 = indexAt(col, row + 1);
      const i10 = indexAt(col + 1, row);
      const i11 = indexAt(col + 1, row + 1);
      // Match VertexNet.kt:
      //   faceA = (col,row), (col,row+1), (col+1,row)
      //   faceB = (col+1,row), (col,row+1), (col+1,row+1)
      indices[triIdx++] = i00; indices[triIdx++] = i01; indices[triIdx++] = i10;
      indices[triIdx++] = i10; indices[triIdx++] = i01; indices[triIdx++] = i11;
    }
  }

  // 4. Skirts: emit perimeter vertices + faces. Each skirt vertex inherits its
  //    parent's normal; the position drops straight down by skirtDepth.
  const skirtDepth = Math.max(cellSizeX, cellSizeZ) * 4;
  const skirtBase = baseVertCount;

  // Layout the skirt vertices:
  //   south[0..cols-1]   → indices [skirtBase + 0 .. skirtBase + cols - 1]
  //   north[0..cols-1]   → [skirtBase + cols .. skirtBase + 2*cols - 1]
  //   west[1..rows-2]    → [skirtBase + 2*cols .. skirtBase + 2*cols + rows - 3]
  //   east[1..rows-2]    → [skirtBase + 2*cols + rows - 2 .. ]
  // The west/east strips skip row 0 and row gridHeight (those parent verts
  // already have skirt twins in south[col=0]/[col=gridWidth] and similar for north).
  const southStart = skirtBase;
  const northStart = skirtBase + cols;
  const westStart = skirtBase + 2 * cols;
  const eastStart = skirtBase + 2 * cols + (rows - 2);

  const writeSkirtVertex = (
    skirtVi: number,
    parentVi: number,
  ): void => {
    const ps = parentVi * 3;
    const pd = skirtVi * 3;
    positions[pd] = positions[ps];
    positions[pd + 1] = positions[ps + 1] - skirtDepth;
    positions[pd + 2] = positions[ps + 2];
    normals[pd] = normals[ps];
    normals[pd + 1] = normals[ps + 1];
    normals[pd + 2] = normals[ps + 2];
  };

  // South + North full strips
  for (let col = 0; col < cols; col++) {
    writeSkirtVertex(southStart + col, indexAt(col, 0));
    writeSkirtVertex(northStart + col, indexAt(col, gridHeight));
  }
  // West + East interior strips (skip row 0 and row gridHeight)
  for (let row = 1; row < rows - 1; row++) {
    writeSkirtVertex(westStart + (row - 1), indexAt(0, row));
    writeSkirtVertex(eastStart + (row - 1), indexAt(gridWidth, row));
  }

  // Skirt parent index resolver for each perimeter edge (handles corners via S/N).
  const westSkirtAt = (row: number): number => {
    if (row === 0) return southStart + 0;
    if (row === gridHeight) return northStart + 0;
    return westStart + (row - 1);
  };
  const eastSkirtAt = (row: number): number => {
    if (row === 0) return southStart + gridWidth;
    if (row === gridHeight) return northStart + gridWidth;
    return eastStart + (row - 1);
  };

  // South edge skirts (z=min). Match Kotlin: skirts.add(p0, s1, s0); skirts.add(p0, p1, s1)
  for (let col = 0; col < gridWidth; col++) {
    const p0 = indexAt(col, 0);
    const p1 = indexAt(col + 1, 0);
    const s0 = southStart + col;
    const s1 = southStart + col + 1;
    indices[triIdx++] = p0; indices[triIdx++] = s1; indices[triIdx++] = s0;
    indices[triIdx++] = p0; indices[triIdx++] = p1; indices[triIdx++] = s1;
  }
  // North edge skirts. Kotlin: skirts.add(p0, s0, s1); skirts.add(p0, s1, p1)
  for (let col = 0; col < gridWidth; col++) {
    const p0 = indexAt(col, gridHeight);
    const p1 = indexAt(col + 1, gridHeight);
    const s0 = northStart + col;
    const s1 = northStart + col + 1;
    indices[triIdx++] = p0; indices[triIdx++] = s0; indices[triIdx++] = s1;
    indices[triIdx++] = p0; indices[triIdx++] = s1; indices[triIdx++] = p1;
  }
  // West edge skirts. Kotlin: skirts.add(p0, s0, s1); skirts.add(p0, s1, p1)
  for (let row = 0; row < gridHeight; row++) {
    const p0 = indexAt(0, row);
    const p1 = indexAt(0, row + 1);
    const s0 = westSkirtAt(row);
    const s1 = westSkirtAt(row + 1);
    indices[triIdx++] = p0; indices[triIdx++] = s0; indices[triIdx++] = s1;
    indices[triIdx++] = p0; indices[triIdx++] = s1; indices[triIdx++] = p1;
  }
  // East edge skirts. Kotlin: skirts.add(p0, s1, s0); skirts.add(p0, p1, s1)
  for (let row = 0; row < gridHeight; row++) {
    const p0 = indexAt(gridWidth, row);
    const p1 = indexAt(gridWidth, row + 1);
    const s0 = eastSkirtAt(row);
    const s1 = eastSkirtAt(row + 1);
    indices[triIdx++] = p0; indices[triIdx++] = s1; indices[triIdx++] = s0;
    indices[triIdx++] = p0; indices[triIdx++] = p1; indices[triIdx++] = s1;
  }

  // Truncate to the actual indices written. When no quads were skipped (no
  // holes, or no overlapping holes), `triIdx === indices.length` and `slice`
  // returns a same-length view; with holes the trailing slack is dropped.
  const finalIndices = triIdx === indices.length ? indices : indices.slice(0, triIdx);

  return {
    positions,
    normals,
    uvs,
    indices: finalIndices,
    vertexCount: totalVertCount,
    triangleCount: triIdx / 3,
  };
}

/**
 * Ray-cast point-in-polygon for an interleaved XZ vertex buffer (`x0, z0,
 * x1, z1, ...`). Standard horizontal half-line algorithm; boundary is
 * treated "inside" since the brush footprint is already half-open.
 */
function pointInPolygonXZ(x: number, z: number, verts: Float32Array): boolean {
  const n = verts.length >> 1;
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = verts[i * 2];
    const zi = verts[i * 2 + 1];
    const xj = verts[j * 2];
    const zj = verts[j * 2 + 1];
    const intersect =
      zi > z !== zj > z && x < ((xj - xi) * (z - zi)) / (zj - zi || 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Sample `src` across a (2*radius+1)² grid of LOD0-resolution taps centred on
 * (x, z); return the maximum. Mirrors `TerrainBuilder.sampleMaxPooled`.
 */
function sampleMaxPooled(
  src: HeightSampleFn,
  x: number,
  z: number,
  radius: number,
  step: number,
): number {
  let maxY = Number.NEGATIVE_INFINITY;
  for (let dx = -radius; dx <= radius; dx++) {
    for (let dz = -radius; dz <= radius; dz++) {
      const y = src(x + dx * step, z + dz * step);
      if (y > maxY) maxY = y;
    }
  }
  return maxY;
}

function applyAnalyticNormals(
  positions: Float32Array,
  normals: Float32Array,
  baseVertCount: number,
  src: HeightSampleFn,
  eps: number,
): void {
  const invTwoEps = 1 / (2 * eps);
  for (let i = 0; i < baseVertCount; i++) {
    const x = positions[i * 3];
    const z = positions[i * 3 + 2];
    const hxp = src(x + eps, z);
    const hxm = src(x - eps, z);
    const hzp = src(x, z + eps);
    const hzm = src(x, z - eps);
    const dhdx = (hxp - hxm) * invTwoEps;
    const dhdz = (hzp - hzm) * invTwoEps;
    // n = normalize(-dh/dx, 1, -dh/dz)
    const nx = -dhdx;
    const ny = 1;
    const nz = -dhdz;
    const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
    normals[i * 3] = nx / len;
    normals[i * 3 + 1] = ny / len;
    normals[i * 3 + 2] = nz / len;
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
